import {
  Button,
  Dimmer,
  Dropdown,
  Header,
  Input,
  Loader,
  Message,
  Segment,
  SingleDatePicker,
} from '@mairistem/composants';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import PointageModeReglement from '@/components/Versement/PointageBordereaux/PointageModeReglement';
import ReglementListe from '@/components/Versement/PointageBordereaux/ReglementListe';
import { MODES_REGLEMENTS, MODES_REGLEMENTS_POINTAGE } from '@/constants';
import { ArretePeriodiquePointageContext, ModeReglementContext } from '@/contexts';
import { useReglementPointage } from '@/hooks';
import type { ModeReglementsPointageType, ReglementType } from '@/types';

const PERIODE_OPTIONS = [
  {
    key: 'dernierArrete',
    value: 'dernierArrete',
    text: 'Depuis le dernier arrêté périodique',
  },
  { key: 'libre', value: 'libre', text: 'Sélection libre' },
];

type ArretePeriodiquePointageProps = {
  onGoToListe: () => void;
  onGoToEditions: () => void;
};

const ArretePeriodiquePointage = (props: ArretePeriodiquePointageProps) => {
  const { onGoToListe, onGoToEditions } = props;

  const [modeReglementActif, setModeReglementActif] =
    React.useState<ModeReglementsPointageType | null>(null);

  const { regieModesReglement } = React.useContext(ModeReglementContext);
  const {
    selected: selectedIds,
    loading: loadingSelection,
    saving,
    toggle: toggleSelection,
    applyBulk,
    periodeMode,
    dateDebut,
    dateFin,
    requestPeriodeChange,
  } = React.useContext(ArretePeriodiquePointageContext);

  // Modes de pointage autorisés pour la régie courante.
  const modesReglementsPointageFiltered = React.useMemo(
    () =>
      _.pickBy(MODES_REGLEMENTS_POINTAGE, (_label, code) =>
        _.some(regieModesReglement, (regieMode) => regieMode.code === code),
      ),
    [regieModesReglement],
  );

  // 12 contextes de règlements (un par mode). Fournis par PointageEtEditionsWrapper.
  const contextCheque = useReglementPointage(MODES_REGLEMENTS.CHEQUE);
  const contextNumeraire = useReglementPointage(MODES_REGLEMENTS.NUMERAIRE);
  const contextCESU = useReglementPointage(MODES_REGLEMENTS.CESU);
  const contextChequeVac = useReglementPointage(MODES_REGLEMENTS.CHEQUE_VAC);
  const contextCarteBancaire = useReglementPointage(MODES_REGLEMENTS.CARTE_BANCAIRE);
  const contextVirement = useReglementPointage(MODES_REGLEMENTS.VIREMENT);
  const contextPrelevement = useReglementPointage(MODES_REGLEMENTS.PRELEVEMENT);
  const contextCarteBancaireInternet = useReglementPointage(
    MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET,
  );
  const contextPrelevementInternet = useReglementPointage(MODES_REGLEMENTS.PRELEVEMENT_INTERNET);
  const contextTicket = useReglementPointage(MODES_REGLEMENTS.TICKET);
  const contextBonCCAS = useReglementPointage(MODES_REGLEMENTS.BON_CCAS);
  const contextBonCAF = useReglementPointage(MODES_REGLEMENTS.BON_CAF);

  const currentContext = React.useMemo(() => {
    switch (modeReglementActif) {
      case MODES_REGLEMENTS.CHEQUE:
        return contextCheque;
      case MODES_REGLEMENTS.NUMERAIRE:
        return contextNumeraire;
      case MODES_REGLEMENTS.CESU:
        return contextCESU;
      case MODES_REGLEMENTS.CHEQUE_VAC:
        return contextChequeVac;
      case MODES_REGLEMENTS.CARTE_BANCAIRE:
        return contextCarteBancaire;
      case MODES_REGLEMENTS.VIREMENT:
        return contextVirement;
      case MODES_REGLEMENTS.PRELEVEMENT:
        return contextPrelevement;
      case MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET:
        return contextCarteBancaireInternet;
      case MODES_REGLEMENTS.PRELEVEMENT_INTERNET:
        return contextPrelevementInternet;
      case MODES_REGLEMENTS.TICKET:
        return contextTicket;
      case MODES_REGLEMENTS.BON_CCAS:
        return contextBonCCAS;
      case MODES_REGLEMENTS.BON_CAF:
        return contextBonCAF;
      default:
        return null;
    }
  }, [
    modeReglementActif,
    contextCheque,
    contextNumeraire,
    contextCESU,
    contextChequeVac,
    contextCarteBancaire,
    contextVirement,
    contextPrelevement,
    contextCarteBancaireInternet,
    contextPrelevementInternet,
    contextTicket,
    contextBonCCAS,
    contextBonCAF,
  ]);

  const currentReglements = React.useMemo(() => currentContext?.reglements ?? [], [currentContext]);

  const hasOneLoading = React.useMemo(
    () =>
      contextCheque.loading ||
      contextNumeraire.loading ||
      contextCESU.loading ||
      contextChequeVac.loading ||
      contextCarteBancaire.loading ||
      contextVirement.loading ||
      contextPrelevement.loading ||
      contextCarteBancaireInternet.loading ||
      contextPrelevementInternet.loading ||
      contextTicket.loading ||
      contextBonCCAS.loading ||
      contextBonCAF.loading ||
      loadingSelection,
    [
      contextCheque.loading,
      contextNumeraire.loading,
      contextCESU.loading,
      contextChequeVac.loading,
      contextCarteBancaire.loading,
      contextVirement.loading,
      contextPrelevement.loading,
      contextCarteBancaireInternet.loading,
      contextPrelevementInternet.loading,
      contextTicket.loading,
      contextBonCCAS.loading,
      contextBonCAF.loading,
      loadingSelection,
    ],
  );

  // Somme des règlements non-annulés de tous les modes (base pour l'activation du bouton Éditions).
  const totalNonAnnulesTousModes = React.useMemo(() => {
    const allReglements = [
      contextCheque.reglements,
      contextNumeraire.reglements,
      contextCESU.reglements,
      contextChequeVac.reglements,
      contextCarteBancaire.reglements,
      contextVirement.reglements,
      contextPrelevement.reglements,
      contextCarteBancaireInternet.reglements,
      contextPrelevementInternet.reglements,
      contextTicket.reglements,
      contextBonCCAS.reglements,
      contextBonCAF.reglements,
    ];
    return allReglements.reduce(
      (acc, reglements) =>
        acc + _.filter(reglements, (r: ReglementType) => _.isNil(r.dateAnnulationRecu)).length,
      0,
    );
  }, [
    contextCheque.reglements,
    contextNumeraire.reglements,
    contextCESU.reglements,
    contextChequeVac.reglements,
    contextCarteBancaire.reglements,
    contextVirement.reglements,
    contextPrelevement.reglements,
    contextCarteBancaireInternet.reglements,
    contextPrelevementInternet.reglements,
    contextTicket.reglements,
    contextBonCCAS.reglements,
    contextBonCAF.reglements,
  ]);

  // Items sélectionnés pour ReglementListe : règlements du mode courant qui sont soit annulés
  // (pris d'office) soit présents dans le Set du contexte de sélection.
  const selectedItems = React.useMemo<ReglementType[]>(
    () =>
      _.filter(
        currentReglements,
        (r: ReglementType) => !_.isNil(r.dateAnnulationRecu) || selectedIds.has(r.identifiant),
      ),
    [currentReglements, selectedIds],
  );

  const handleModeReglementChange = React.useCallback((_e, { value }) => {
    setModeReglementActif(value);
  }, []);

  const handlePeriodeModeChange = React.useCallback(
    (_e, { value }) => requestPeriodeChange({ periodeMode: value }),
    [requestPeriodeChange],
  );

  const handleDateFinChange = React.useCallback(
    (val: dayjs.Dayjs | null) => {
      if (val) requestPeriodeChange({ dateFin: val });
    },
    [requestPeriodeChange],
  );

  const disabledDateFin = React.useCallback((d: dayjs.Dayjs) => d.isAfter(dayjs(), 'day'), []);

  /**
   * Reçoit les événements de la liste :
   *  - `selectedReglement === null && selectedReglements.length > 0` : "cocher tout"
   *  - `selectedReglement === null && selectedReglements.length === 0` : "décocher tout"
   *  - sinon : toggle unitaire
   */
  const handleReglementSelect = React.useCallback(
    async (selectedReglement: ReglementType | null, nextReglements: ReglementType[]) => {
      if (_.isNil(selectedReglement)) {
        // Bulk : on calcule la nouvelle sélection API (uniquement les non-annulés de TOUS les modes).
        const idsDuMode = _.map(
          _.filter(nextReglements, (r) => _.isNil(r.dateAnnulationRecu)),
          'identifiant',
        );
        // Conserver la sélection des autres modes + remplacer celle du mode courant.
        const idsDuModeCourantAvant = new Set(
          _.map(
            _.filter(currentReglements, (r) => _.isNil(r.dateAnnulationRecu)),
            'identifiant',
          ),
        );
        const idsAutresModes = Array.from(selectedIds).filter(
          (id) => !idsDuModeCourantAvant.has(id),
        );
        const nextGlobal = [...idsAutresModes, ...idsDuMode];
        await applyBulk(nextGlobal);
        return;
      }

      // Toggle unitaire : on ne persiste pas les annulés (ils sont pris d'office côté back).
      if (!_.isNil(selectedReglement.dateAnnulationRecu)) return;

      const isNowChecked = _.some(
        nextReglements,
        (r) => r.identifiant === selectedReglement.identifiant,
      );
      await toggleSelection(selectedReglement.identifiant, isNowChecked);
    },
    [applyBulk, currentReglements, selectedIds, toggleSelection],
  );

  const handleRecuAnnulation = React.useCallback(() => {
    // Recharger tous les contextes : le reçu annulé devient "rouge" dans la liste adéquate.
    // Le back a déjà supprimé les pointages côté API via RecuRepository::annulation.
    contextCheque.reset();
    contextNumeraire.reset();
    contextCESU.reset();
    contextChequeVac.reset();
    contextCarteBancaire.reset();
    contextVirement.reset();
    contextPrelevement.reset();
    contextCarteBancaireInternet.reset();
    contextPrelevementInternet.reset();
    contextTicket.reset();
    contextBonCCAS.reset();
    contextBonCAF.reset();
  }, [
    contextCheque,
    contextNumeraire,
    contextCESU,
    contextChequeVac,
    contextCarteBancaire,
    contextVirement,
    contextPrelevement,
    contextCarteBancaireInternet,
    contextPrelevementInternet,
    contextTicket,
    contextBonCCAS,
    contextBonCAF,
  ]);

  const getTotalByCode = React.useCallback(
    (code: ModeReglementsPointageType) => {
      const mapping: Record<string, ReglementType[]> = {
        [MODES_REGLEMENTS.CHEQUE]: contextCheque.reglements,
        [MODES_REGLEMENTS.NUMERAIRE]: contextNumeraire.reglements,
        [MODES_REGLEMENTS.CESU]: contextCESU.reglements,
        [MODES_REGLEMENTS.CHEQUE_VAC]: contextChequeVac.reglements,
        [MODES_REGLEMENTS.CARTE_BANCAIRE]: contextCarteBancaire.reglements,
        [MODES_REGLEMENTS.VIREMENT]: contextVirement.reglements,
        [MODES_REGLEMENTS.PRELEVEMENT]: contextPrelevement.reglements,
        [MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET]: contextCarteBancaireInternet.reglements,
        [MODES_REGLEMENTS.PRELEVEMENT_INTERNET]: contextPrelevementInternet.reglements,
        [MODES_REGLEMENTS.TICKET]: contextTicket.reglements,
        [MODES_REGLEMENTS.BON_CCAS]: contextBonCCAS.reglements,
        [MODES_REGLEMENTS.BON_CAF]: contextBonCAF.reglements,
      };
      return _.filter(mapping[code] ?? [], (r) => _.isNil(r.dateAnnulationRecu)).length;
    },
    [
      contextCheque.reglements,
      contextNumeraire.reglements,
      contextCESU.reglements,
      contextChequeVac.reglements,
      contextCarteBancaire.reglements,
      contextVirement.reglements,
      contextPrelevement.reglements,
      contextCarteBancaireInternet.reglements,
      contextPrelevementInternet.reglements,
      contextTicket.reglements,
      contextBonCCAS.reglements,
      contextBonCAF.reglements,
    ],
  );

  // Initialise le premier mode actif.
  React.useEffect(() => {
    if (_.isNil(modeReglementActif) && !_.isEmpty(modesReglementsPointageFiltered)) {
      const premierMode = _.head(
        _.keys(modesReglementsPointageFiltered),
      ) as ModeReglementsPointageType;
      setModeReglementActif(premierMode);
    }
  }, [modeReglementActif, modesReglementsPointageFiltered]);

  const selectedCount = selectedIds.size;
  const canAccessEditions =
    !hasOneLoading && (totalNonAnnulesTousModes === 0 || selectedCount >= totalNonAnnulesTousModes);
  const aucunReglement = !hasOneLoading && totalNonAnnulesTousModes === 0;

  // Sélecteur de période (dropdown + dates en sélection libre). Réutilisé tel quel qu'il y ait
  // des règlements (intercalé dans PointageModeReglement, entre le mode et le compteur) ou non
  // (affiché seul, le dropdown de mode étant alors masqué).
  const periodeSelectorNode = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5em',
        marginRight: '1em',
        verticalAlign: 'middle',
      }}
    >
      <Dropdown
        selection
        value={periodeMode}
        options={PERIODE_OPTIONS}
        onChange={handlePeriodeModeChange}
        style={{ minWidth: 260 }}
      />
      {periodeMode === 'libre' ? (
        <>
          <span>Du</span>
          <Input
            disabled
            value={dateDebut ? dateDebut.format('DD/MM/YYYY') : '—'}
            style={{ width: 140 }}
          />
          <span>au</span>
          <SingleDatePicker
            inputReadOnly
            changeOnBlur={false}
            value={dateFin}
            onChange={handleDateFinChange}
            disabledDate={disabledDateFin}
          />
        </>
      ) : null}
    </span>
  );

  if (hasOneLoading && _.isNil(modeReglementActif)) {
    return (
      <Dimmer active inverted>
        <Loader inverted>Chargement des règlements de la période</Loader>
      </Dimmer>
    );
  }

  return (
    <Segment
      inverted
      secondary
      style={{
        flex: 1,
        overflow: 'auto',
        background: 'transparent',
      }}
    >
      <Segment style={{ borderRadius: 14, display: 'flex', flexDirection: 'column' }}>
        <Header as="h1" textAlign="center" style={{ marginBottom: '1em' }}>
          <Header.Content>Contrôle des règlements de la période</Header.Content>
        </Header>

        {/* Barre : à gauche le dropdown de mode de règlement, puis (intercalé) le sélecteur de
            période, puis le nombre de règlements. Quand aucun règlement n'est à contrôler, le
            dropdown de mode (vide) est COMPLÈTEMENT masqué : on n'affiche que le sélecteur de
            période, pour pouvoir changer d'option / de date. */}
        <div style={{ padding: '0 1em', marginBottom: '1em' }}>
          {!aucunReglement && modeReglementActif ? (
            <PointageModeReglement
              modeReglementActif={modeReglementActif}
              loading={hasOneLoading}
              disabled={hasOneLoading}
              getTotalByCode={getTotalByCode}
              onModeReglementChange={handleModeReglementChange}
              periodeSelector={periodeSelectorNode}
            />
          ) : (
            periodeSelectorNode
          )}
        </div>

        {aucunReglement ? (
          <Message info style={{ alignSelf: 'center', maxWidth: 600 }}>
            <Message.Header>Aucun règlement à contrôler</Message.Header>
            <p>
              Aucun règlement n&apos;est en attente de contrôle pour la période{' '}
              {periodeMode === 'libre' ? 'sélectionnée' : 'en cours'}.
            </p>
          </Message>
        ) : (
          <div className={saving ? 'arrete-pointage-saving' : undefined}>
            {/* Pendant un toggle en vol, on désactive uniquement les cellules checkbox
                (lignes et header) sans bloquer les autres actions (changement de mode,
                export CSV/PDF, ouverture d'un reçu). Ciblage via les data-name exposés
                par @mairistem/liste : "checkbox" (par ligne) et "selection" (header). */}
            <style>
              {`.arrete-pointage-saving [data-name="checkbox"],
                .arrete-pointage-saving [data-name="selection"] {
                  pointer-events: none;
                  opacity: 0.4;
                  transition: opacity 120ms ease;
                }`}
            </style>
            {modeReglementActif ? (
              <ReglementListe
                type={modeReglementActif}
                onSelect={handleReglementSelect}
                selectedItems={selectedItems}
                onRecuAnnulation={handleRecuAnnulation}
              />
            ) : null}
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '1em',
            paddingRight: '1em',
            gap: '1em',
          }}
        >
          <Button
            content="Revenir à la liste des arrêtés"
            icon="chevron left"
            onClick={onGoToListe}
            basic
          />
          <Button
            positive
            size="large"
            content="Accéder aux éditions d'arrêté"
            disabled={!canAccessEditions}
            onClick={onGoToEditions}
          />
        </div>
      </Segment>
    </Segment>
  );
};

export default ArretePeriodiquePointage;
